import {
    effect,
    inject,
    Injectable,
    InjectionToken,
    signal,
    type Provider,
    type Signal,
} from '@angular/core';
import { UserStore } from '@quipex/core/auth';
import type {
    BuildingPrimaryPropertyType,
    BuildingSubPremiseType,
} from '@quipex/shared/contracts';

export interface RecentBuilding {
    readonly id: string;
    readonly name: string;
    readonly address: string;
    readonly primaryPropertyType: BuildingPrimaryPropertyType;
    readonly subPremiseType?: BuildingSubPremiseType | null;
}

export interface RecentBuildings {
    readonly recentBuildings: Signal<RecentBuilding[]>;
    markOpened(building: RecentBuilding): void;
    refresh(building: RecentBuilding): void;
    remove(buildingId: string): void;
    clear(): void;
}

export const RECENT_BUILDINGS = new InjectionToken<RecentBuildings>(
    'RecentBuildings'
);

@Injectable()
class RecentBuildingsStore implements RecentBuildings {
    //#region Dependency injection
    private readonly userStore = inject(UserStore);
    //#endregion

    //#region Constants
    private readonly maxRecentBuildings = 5;
    //#endregion

    //#region State
    private readonly _recentBuildings = signal<RecentBuilding[]>([]);
    public readonly recentBuildings = this._recentBuildings.asReadonly();
    private previousOrgId: string | null | undefined;
    //#endregion

    //#region Lifecycle hooks
    constructor() {
        effect(() => {
            const orgId = this.userStore.org()?.id ?? null;
            if (
                this.previousOrgId !== undefined &&
                this.previousOrgId !== orgId
            ) {
                this.clear();
            }

            this.previousOrgId = orgId;
        });
    }
    //#endregion

    //#region Public methods
    public markOpened(building: RecentBuilding): void {
        this._recentBuildings.update((buildings) =>
            [
                building,
                ...buildings.filter((item) => item.id !== building.id),
            ].slice(0, this.maxRecentBuildings)
        );
    }

    public refresh(building: RecentBuilding): void {
        this._recentBuildings.update((buildings) =>
            buildings.map((item) => (item.id === building.id ? building : item))
        );
    }

    public remove(buildingId: string): void {
        this._recentBuildings.update((buildings) =>
            buildings.filter((building) => building.id !== buildingId)
        );
    }

    public clear(): void {
        this._recentBuildings.set([]);
    }
    //#endregion
}

export function injectRecentBuildings(): RecentBuildings {
    return inject(RECENT_BUILDINGS);
}

export function provideRecentBuildings(): Provider[] {
    return [
        {
            provide: RECENT_BUILDINGS,
            useClass: RecentBuildingsStore,
        },
    ];
}
